/**
 * Guided calibration.
 *
 * The user sweeps each hand through a comfortable near-to-far range while we record the
 * hand-to-antenna distance. Percentile trimming discards the outliers that inevitably
 * appear when a hand leaves frame or the detector briefly latches onto a face.
 *
 * Calibration is entirely optional: `DEFAULT_SETTINGS` already contains a usable range.
 */

import { SETTINGS_LIMITS } from '../app/settings';
import type { ControlRange } from '../app/settings';

export type CalibrationStepId = 'bothHands' | 'assignRoles' | 'pitchSweep' | 'volumeSweep' | 'done';

export interface CalibrationStepDefinition {
  id: CalibrationStepId;
  title: string;
  instruction: string;
  /** Steps that collect distance samples. */
  collects: 'pitch' | 'volume' | null;
  /** Samples needed before the step can complete. */
  requiredSamples: number;
}

export const CALIBRATION_STEPS: readonly CalibrationStepDefinition[] = [
  {
    id: 'bothHands',
    title: 'Show both hands',
    instruction: 'Hold both hands up so the camera can see them, then continue.',
    collects: null,
    requiredSamples: 0,
  },
  {
    id: 'assignRoles',
    title: 'Check your hands',
    instruction:
      'The hand nearest the pitch antenna controls pitch. Use Swap hands if it feels wrong, then continue.',
    collects: null,
    requiredSamples: 0,
  },
  {
    id: 'pitchSweep',
    title: 'Sweep the pitch hand',
    instruction:
      'Move the pitch hand slowly from as close to the vertical antenna as is comfortable, out to as far as you can reach.',
    collects: 'pitch',
    requiredSamples: 45,
  },
  {
    id: 'volumeSweep',
    title: 'Sweep the volume hand',
    instruction:
      'Now move the volume hand from just above the loop out to your comfortable maximum.',
    collects: 'volume',
    requiredSamples: 45,
  },
  {
    id: 'done',
    title: 'Calibrated',
    instruction: 'Ranges saved.',
    collects: null,
    requiredSamples: 0,
  },
];

export interface CalibrationResult {
  pitchRange: ControlRange;
  volumeRange: ControlRange;
}

/**
 * Robust min/max from a sample set.
 *
 * Uses the 5th/95th percentiles so a handful of spurious readings cannot stretch the range,
 * and enforces a minimum span so a user who barely moved still gets a playable instrument.
 */
export function robustRange(
  samples: readonly number[],
  fallback: ControlRange,
  lowPercentile = 0.05,
  highPercentile = 0.95,
): ControlRange {
  const clean = samples.filter((value) => Number.isFinite(value) && value >= 0);
  if (clean.length < 5) return { ...fallback };

  const sorted = [...clean].sort((a, b) => a - b);
  const pick = (p: number): number => {
    const index = Math.min(sorted.length - 1, Math.max(0, Math.round(p * (sorted.length - 1))));
    return sorted[index] ?? 0;
  };

  let near = pick(lowPercentile);
  let far = pick(highPercentile);
  if (far < near) [near, far] = [far, near];

  if (far - near < SETTINGS_LIMITS.minRangeSpan) {
    const centre = (near + far) / 2;
    const half = SETTINGS_LIMITS.minRangeSpan / 2;
    near = Math.max(0, centre - half);
    far = near + SETTINGS_LIMITS.minRangeSpan;
  }
  return { near, far };
}

export interface CalibrationSnapshot {
  step: CalibrationStepDefinition;
  index: number;
  total: number;
  progress: number;
  samples: number;
  canAdvance: boolean;
}

export interface CalibrationOptions {
  /** Ranges to fall back to when a sweep yields too little data. */
  fallback: CalibrationResult;
  /** Hard cap on retained samples so a long sweep cannot grow unbounded. */
  maxSamples?: number;
}

/**
 * Pure state machine — it is fed distances by the interaction layer and knows nothing about
 * the DOM, so the whole flow is unit-testable with synthetic sequences.
 */
export class CalibrationController {
  private stepIndex = 0;

  private readonly pitchSamples: number[] = [];

  private readonly volumeSamples: number[] = [];

  private readonly maxSamples: number;

  private fallback: CalibrationResult;

  private running = false;

  /** True once both hands have been seen during the `bothHands` step. */
  private bothHandsSeen = false;

  constructor(options: CalibrationOptions) {
    this.fallback = {
      pitchRange: { ...options.fallback.pitchRange },
      volumeRange: { ...options.fallback.volumeRange },
    };
    this.maxSamples = options.maxSamples ?? 600;
  }

  get active(): boolean {
    return this.running;
  }

  get currentStep(): CalibrationStepDefinition {
    return CALIBRATION_STEPS[this.stepIndex] ?? CALIBRATION_STEPS[CALIBRATION_STEPS.length - 1]!;
  }

  setFallback(fallback: CalibrationResult): void {
    this.fallback = {
      pitchRange: { ...fallback.pitchRange },
      volumeRange: { ...fallback.volumeRange },
    };
  }

  start(): void {
    this.running = true;
    this.stepIndex = 0;
    this.pitchSamples.length = 0;
    this.volumeSamples.length = 0;
    this.bothHandsSeen = false;
  }

  cancel(): void {
    this.running = false;
    this.stepIndex = 0;
    this.pitchSamples.length = 0;
    this.volumeSamples.length = 0;
    this.bothHandsSeen = false;
  }

  /**
   * Feed one frame of live measurements.
   *
   * @param pitchDistance  hand-to-pitch-antenna distance, or null when that hand is missing
   * @param volumeDistance hand-to-volume-loop distance, or null when that hand is missing
   */
  sample(pitchDistance: number | null, volumeDistance: number | null): void {
    if (!this.running) return;
    const step = this.currentStep;

    if (step.id === 'bothHands' && pitchDistance !== null && volumeDistance !== null) {
      this.bothHandsSeen = true;
    }
    if (step.collects === 'pitch' && pitchDistance !== null) {
      this.push(this.pitchSamples, pitchDistance);
    }
    if (step.collects === 'volume' && volumeDistance !== null) {
      this.push(this.volumeSamples, volumeDistance);
    }
  }

  private push(target: number[], value: number): void {
    if (!Number.isFinite(value) || value < 0) return;
    if (target.length >= this.maxSamples) target.shift();
    target.push(value);
  }

  private samplesForStep(step: CalibrationStepDefinition): number {
    if (step.collects === 'pitch') return this.pitchSamples.length;
    if (step.collects === 'volume') return this.volumeSamples.length;
    return 0;
  }

  get snapshot(): CalibrationSnapshot {
    const step = this.currentStep;
    const samples = this.samplesForStep(step);
    const progress =
      step.requiredSamples > 0
        ? Math.min(1, samples / step.requiredSamples)
        : this.staticProgress(step);
    return {
      step,
      index: this.stepIndex,
      total: CALIBRATION_STEPS.length - 1,
      progress,
      samples,
      canAdvance: this.canAdvance(),
    };
  }

  private staticProgress(step: CalibrationStepDefinition): number {
    if (step.id === 'bothHands') return this.bothHandsSeen ? 1 : 0;
    return 1;
  }

  canAdvance(): boolean {
    const step = this.currentStep;
    if (step.id === 'bothHands') return this.bothHandsSeen;
    if (step.requiredSamples > 0) return this.samplesForStep(step) >= step.requiredSamples;
    return true;
  }

  /**
   * Move to the next step. Returns the finished result when calibration completes.
   * Advancing early is allowed (the user may not be able to complete a sweep); in that case
   * the fallback range is used for whatever was not measured.
   */
  advance(): CalibrationResult | null {
    if (!this.running) return null;
    if (this.stepIndex < CALIBRATION_STEPS.length - 1) this.stepIndex += 1;
    if (this.currentStep.id !== 'done') return null;
    this.running = false;
    return this.finish();
  }

  /** Compute the result from whatever samples exist. Safe to call at any time. */
  finish(): CalibrationResult {
    return {
      pitchRange: robustRange(this.pitchSamples, this.fallback.pitchRange),
      volumeRange: robustRange(this.volumeSamples, this.fallback.volumeRange),
    };
  }
}
